/**
 * netdiag.ts — always-on, allocation-free counters for the online session
 * (M73). Every "the car stutters/teleports" report needs DATA, not theories:
 * these counters say WHICH mechanism moved the car — corrections (and how
 * big), clock dilation, hard resyncs, server-side input misscheduling (ack
 * lag), snapshot gaps — so a live session can be diagnosed from the debug
 * overlay (?debug=1) or the console (`__drNet`).
 *
 * Costs a handful of number bumps per snapshot — nothing on the hot path.
 */

export interface NetDiag {
  /** Snapshots applied + worst/last gap between them (ms). */
  snaps: number;
  snapGapMaxMs: number;
  snapGapLastMs: number;
  /** LOCAL-car correction sizes per reconcile (u): buckets + running max.
   *  clean <0.05 (bit-exact prediction), small <1, med <6, big ≥6, snapped =
   *  the speed-relative cap zeroed the offset (a visible teleport). */
  corrClean: number;
  corrSmall: number;
  corrMed: number;
  corrBig: number;
  corrSnapped: number;
  corrMax: number;
  corrLast: number;
  /** Replay backlog per reconcile (ticks). */
  replayLast: number;
  replayMax: number;
  /** Server-reported input margin (ticks): last + extremes + EMA mirror. */
  marginLast: number;
  marginMin: number;
  marginMax: number;
  /** How far the server's last-applied input tick lagged the snapshot tick.
   *  0 = perfectly scheduled every step; >0 = the server held/fell back
   *  (inputs arriving late or misscheduled) — THE scheduling-health signal. */
  ackLagLast: number;
  ackLagMax: number;
  ackLagged: number; // reconciles with lag > 0
  /** Clock moves (M143): `doubles` = extra ticks added in small advances,
   *  `skips` = ticks given back, `resyncs` = BIG moves — an advance of more
   *  than 8 ticks at once, or a label jump after a long freeze. A healthy
   *  race shows a handful of doubles/skips and resyncs near zero. */
  doubles: number;
  skips: number;
  resyncs: number;
  /** Round trip measured on the WebTransport lane itself (ms), or -1. */
  rttWt: number;
  /** The clock's current view of the link: worst and typical recent input
   *  margin (ticks), for the overlay + the diag report. */
  marginWorst: number;
  marginTyp: number;
  /** V2 Phase C (M108): the WebTransport datagram lane — snapshots received
   *  over it, whether it currently carries the stream (promoted), and how
   *  often it was promoted/demoted this race. */
  wtSnaps: number;
  wtActive: number;
  wtPromotions: number;
  wtDemotes: number;
  /** M111d: the furthest stage the lane reached this session — the remote
   *  debugger for platforms we can't attach to (it rides the {t:'diag'}
   *  report into the room server's logs). Stages, in order: none → ctor →
   *  ready → hello (writer up, hello sending) → data (first datagram read).
   *  Failure tags: no-api, ctor-fail, ready-fail, rel:<mode> (reliability
   *  gate closed it), no-writable, hello-fail. */
  wtStage: string;
  /** Loading round (online feel): physics ticks the render loop DROPPED after
   *  main-thread stalls, and how many of them the local catch-up re-simulated. */
  droppedTicks: number;
  catchUpTicks: number;
  /** Loading round (perf telemetry): rendered-frame timing while racing — an
   *  allocation-free histogram (1 ms bins to 254 ms, bin 255 = longer), plus
   *  the draws the native 60 Hz cap skipped. Percentiles come from
   *  frameStats() at report time. Every stutter report finally carries the
   *  main-thread picture the netcode counters alone could never show. */
  frameHist: Uint32Array;
  frames: number;
  frameMaxMs: number;
  rendersSkipped: number;
  /** Loading round: how many times dynamic resolution changed this race. */
  dynResChanges: number;
}

const zero = (): NetDiag => ({
  snaps: 0,
  snapGapMaxMs: 0,
  snapGapLastMs: 0,
  corrClean: 0,
  corrSmall: 0,
  corrMed: 0,
  corrBig: 0,
  corrSnapped: 0,
  corrMax: 0,
  corrLast: 0,
  replayLast: 0,
  replayMax: 0,
  marginLast: 0,
  marginMin: 999,
  marginMax: -999,
  ackLagLast: 0,
  ackLagMax: 0,
  ackLagged: 0,
  doubles: 0,
  skips: 0,
  resyncs: 0,
  rttWt: -1,
  marginWorst: 0,
  marginTyp: 0,
  wtSnaps: 0,
  wtActive: 0,
  wtPromotions: 0,
  wtDemotes: 0,
  wtStage: 'none',
  droppedTicks: 0,
  catchUpTicks: 0,
  frameHist: new Uint32Array(256),
  frames: 0,
  frameMaxMs: 0,
  rendersSkipped: 0,
  dynResChanges: 0,
});

/** The live session's counters (reset on every new WsDriver). */
export const netdiag: NetDiag = zero();

export function resetNetdiag(): void {
  Object.assign(netdiag, zero());
}

// Console access for live debugging: `__drNet` in devtools.
(globalThis as { __drNet?: NetDiag }).__drNet = netdiag;

/**
 * A coarse "what is this player on" tag for the race report (M143): the
 * operating system plus the browser family, or "-app" for the store build —
 * e.g. `mac-chrome`, `ios-safari`, `ios-app`. Deliberately coarse (no version
 * numbers, nothing that identifies a person or a device): the two browser
 * engines carry online races differently, so a lag report is only readable
 * once we know which one it came from. Pure — unit-tested.
 */
export function clientPlatform(ua: string, native: boolean): string {
  const os = /iPhone|iPad|iPod/.test(ua)
    ? 'ios'
    : /Android/.test(ua)
      ? 'android'
      : /Macintosh|Mac OS X/.test(ua)
        ? 'mac'
        : /Windows/.test(ua)
          ? 'win'
          : /CrOS/.test(ua)
            ? 'cros'
            : /Linux/.test(ua)
              ? 'linux'
              : 'other';
  if (native) return `${os}-app`;
  const browser = /Edg(e|A|iOS)?\//.test(ua)
    ? 'edge'
    : /OPR\//.test(ua)
      ? 'opera'
      : /Firefox\/|FxiOS\//.test(ua)
        ? 'firefox'
        : /Chrome\/|CriOS\//.test(ua)
          ? 'chrome'
          : /Safari\//.test(ua)
            ? 'safari'
            : 'other';
  return `${os}-${browser}`;
}

/** Record one rendered frame's duration (ms) into the histogram. */
export function recordFrame(ms: number): void {
  const d = netdiag;
  const bin = ms < 0 ? 0 : ms >= 255 ? 255 : Math.floor(ms);
  d.frameHist[bin]++;
  d.frames++;
  if (ms > d.frameMaxMs) d.frameMaxMs = ms;
}

/** Percentiles + long-frame counts from the histogram (cheap, report-time). */
export function frameStats(): {
  n: number;
  p50: number;
  p95: number;
  p99: number;
  over50: number;
  over100: number;
  max: number;
} {
  const d = netdiag;
  const n = d.frames;
  const pct = (p: number): number => {
    if (n === 0) return 0;
    const target = Math.ceil(p * n);
    let acc = 0;
    for (let i = 0; i < 256; i++) {
      acc += d.frameHist[i];
      if (acc >= target) return i;
    }
    return 255;
  };
  let over50 = 0;
  let over100 = 0;
  for (let i = 50; i < 256; i++) {
    over50 += d.frameHist[i];
    if (i >= 100) over100 += d.frameHist[i];
  }
  return { n, p50: pct(0.5), p95: pct(0.95), p99: pct(0.99), over50, over100, max: Math.round(d.frameMaxMs) };
}

/** One overlay-friendly summary line per concern. */
export function netdiagLines(): [string, string][] {
  const d = netdiag;
  const f = frameStats();
  return [
    [
      'perf frame',
      `p50 ${f.p50} p95 ${f.p95} p99 ${f.p99} | >50 ${f.over50} >100 ${f.over100} max ${f.max} | drop ${d.droppedTicks} catch ${d.catchUpTicks} skipR ${d.rendersSkipped}`,
    ],
    [
      'net corr',
      `${d.corrLast.toFixed(2)}u max ${d.corrMax.toFixed(1)} | c${d.corrClean} s${d.corrSmall} m${d.corrMed} B${d.corrBig} SNAP${d.corrSnapped}`,
    ],
    [
      'net margin',
      `${d.marginLast} [${d.marginMin}..${d.marginMax}] lag ${d.ackLagLast} (max ${d.ackLagMax}, ${d.ackLagged}x)`,
    ],
    [
      'net clock',
      `+${d.doubles} skip${d.skips} big${d.resyncs} | buffer worst ${d.marginWorst} typ ${d.marginTyp} | replay ${d.replayLast}/${d.replayMax}`,
    ],
    [
      'net snaps',
      `${d.snaps} gap ${d.snapGapLastMs.toFixed(0)}ms max ${d.snapGapMaxMs.toFixed(0)}ms`,
    ],
    [
      'net wt',
      `${d.wtActive ? 'LIVE' : 'off'} snaps ${d.wtSnaps} up ${d.wtPromotions} down ${d.wtDemotes} stage ${d.wtStage} rtt ${d.rttWt < 0 ? '-' : Math.round(d.rttWt)}`,
    ],
  ];
}
